if ( typeof VideoPopup !== 'function' ) {

  class VideoPopup extends HTMLElement {

    constructor(){

      super();

      this.trigger = this.querySelector('[data-js-video-popup-link]');
      this.container = this.querySelector('[data-js-video-popup-container]');
      this.closeButton = this.querySelector('[data-js-video-popup-close]');

      if ( ! this.trigger || ! this.container ) return;

      this.open = this.open.bind(this);
      this.close = this.close.bind(this);
      this.handleFullscreenChange = this.handleFullscreenChange.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);

      this.trigger.addEventListener('click', this.open);
      if ( this.closeButton ) this.closeButton.addEventListener('click', this.close);

    }

    open(e){

      e.preventDefault();
      if ( this.opened ) return;

      const template = this.querySelector('template');
      if ( ! template || ! template.content.firstElementChild ) return;

      this.opened = true;
      this.previouslyFocused = document.activeElement;
      this.mediaRoot = this.closest('[data-video]');
      this.pauseGalleryAutoplay();

      if ( this.classList.contains('video-popup--product-media') ) {
        this.placeholder = document.createComment('video-popup-placeholder');
        this.parentNode.insertBefore(this.placeholder, this);
        document.body.appendChild(this);
      }

      this.activeMedia = template.content.firstElementChild.cloneNode(true);
      this.container.appendChild(this.activeMedia);

      this.blackout = document.createElement('div');
      this.blackout.classList.add('video-popup__blackout');
      this.append(this.blackout);

      this.classList.add('video-opened');
      this.setAttribute('role', 'dialog');
      this.setAttribute('aria-modal', 'true');
      this.trigger.setAttribute('aria-expanded', 'true');
      document.body.classList.add('video-popup-open');

      requestAnimationFrame(()=>{
        if ( this.blackout ) this.blackout.style.opacity = '1';
      });

      this._playMedia(this.mediaRoot);

      document.addEventListener('fullscreenchange', this.handleFullscreenChange);
      document.addEventListener('keydown', this.handleKeydown);

      const video = this.querySelector('video');
      if ( video ) video.addEventListener('webkitendfullscreen', this.close, { once: true });

      if ( this.classList.contains('video-popup--product-media') ) {
        this.enterFullscreen(video);
      }

      if ( this.closeButton ) this.closeButton.focus({ preventScroll: true });

    }

    enterFullscreen(video){

      if ( this.requestFullscreen ) {
        this.fullscreenRequested = true;
        const fullscreenPromise = this.requestFullscreen();
        if ( fullscreenPromise ) {
          fullscreenPromise.catch(()=>{
            this.fullscreenRequested = false;
          });
        }
      } else if ( video && video.webkitEnterFullscreen ) {
        video.webkitEnterFullscreen();
      }

    }

    close(){

      if ( ! this.opened ) return;

      if ( document.fullscreenElement === this && document.exitFullscreen ) {
        const exitPromise = document.exitFullscreen();
        if ( exitPromise ) exitPromise.catch(()=>this.cleanup());
        return;
      }

      const video = this.querySelector('video');
      if ( video && video.webkitDisplayingFullscreen && video.webkitExitFullscreen ) {
        video.webkitExitFullscreen();
        return;
      }

      this.cleanup();

    }

    cleanup(){

      if ( ! this.opened ) return;

      this.opened = false;
      this.fullscreenRequested = false;
      this._pauseMedia();

      this.querySelectorAll('iframe, video').forEach(media=>media.remove());
      if ( this.blackout ) this.blackout.remove();

      this.classList.remove('video-opened');
      this.removeAttribute('role');
      this.removeAttribute('aria-modal');
      this.trigger.setAttribute('aria-expanded', 'false');

      document.removeEventListener('fullscreenchange', this.handleFullscreenChange);
      document.removeEventListener('keydown', this.handleKeydown);

      if ( ! document.querySelector('.video-popup.video-opened') ) {
        document.body.classList.remove('video-popup-open');
      }

      this.resumeGalleryAutoplay();

      if ( this.placeholder && this.placeholder.parentNode ) {
        this.placeholder.parentNode.insertBefore(this, this.placeholder);
        this.placeholder.remove();
      }

      if ( this.previouslyFocused && this.previouslyFocused.focus ) {
        this.previouslyFocused.focus({ preventScroll: true });
      }

      this.activeMedia = null;
      this.blackout = null;
      this.mediaRoot = null;
      this.placeholder = null;

    }

    handleFullscreenChange(){

      if ( this.opened && this.fullscreenRequested && ! document.fullscreenElement ) {
        this.cleanup();
      }

    }

    handleKeydown(e){

      if ( e.key === 'Escape' ) {
        this.close();
        return;
      }

      if ( e.key !== 'Tab' ) return;

      const focusable = [
        this.closeButton,
        ...this.querySelectorAll('video[controls], iframe')
      ].filter(Boolean);
      if ( focusable.length === 0 ) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if ( e.shiftKey && document.activeElement === first ) {
        e.preventDefault();
        last.focus();
      } else if ( ! e.shiftKey && document.activeElement === last ) {
        e.preventDefault();
        first.focus();
      }

    }

    pauseGalleryAutoplay(){

      this.gallerySlider = this.closest('product-page')?.querySelector('css-slider');
      if ( ! this.gallerySlider || ! this.gallerySlider.o ) return;

      const autoplay = parseInt(this.gallerySlider.o.autoplay);
      if ( autoplay <= 0 ) return;

      this.galleryAutoplay = this.gallerySlider.o.autoplay;
      this.gallerySlider.o.autoplay = 0;
      if ( this.gallerySlider._autoplayInterval ) {
        clearInterval(this.gallerySlider._autoplayInterval);
      }

    }

    resumeGalleryAutoplay(){

      if ( ! this.gallerySlider || ! this.galleryAutoplay ) return;

      this.gallerySlider.o.autoplay = this.galleryAutoplay;
      if ( this.gallerySlider._initAutoplay ) this.gallerySlider._initAutoplay();

      this.gallerySlider = null;
      this.galleryAutoplay = null;

    }

    _pauseMedia(){

      this.querySelectorAll('.js-youtube').forEach(video=>{
        video.contentWindow.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
      });
      this.querySelectorAll('.js-vimeo').forEach(video=>{
        video.contentWindow.postMessage('{"method":"pause"}', '*');
      });
      this.querySelectorAll('video').forEach(video=>video.pause());

    }

    _playMedia(media){

			if ( ! media ) return;

			switch ( media.dataset.productMediaType ) {
				case 'video':
					if ( this.querySelector('video') ) {
						const playPromise = this.querySelector('video').play();
						if ( playPromise ) playPromise.catch(()=>{});
					}
					break;
				case 'external_video-youtube':
					if ( this.querySelector('.js-youtube') ) {
						this.querySelector('.js-youtube').contentWindow.postMessage('{"event":"command","func":"' + 'playVideo' + '","args":""}', '*');
					}
					break;
				case 'external_video-vimeo':
					if ( this.querySelector('.js-vimeo') ) {
						this.querySelector('.js-vimeo').contentWindow.postMessage('{"method":"play"}', '*');
					}
					break;
			}
		}

  }

  if ( typeof customElements.get('video-popup') == 'undefined' ) {
    customElements.define('video-popup', VideoPopup);
  }

}
